/**
 * InviteRedirectPage — public route at /i/:slug.
 *
 * Anyone given a Diplomexa invite link lands here. We:
 *   1. Hit /api/invites/<slug>/track to record a click
 *   2. Detect the device from the response (server-side UA parse)
 *   3. Auto-route iOS → TestFlight, Android → APK download, else → /download
 *
 * No JS detection — server-side UA detection is more reliable than navigator.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Loader2 } from "lucide-react";

export default function InviteRedirectPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/invites/${slug}/track`);
        if (cancelled) return;
        // Persist for the /download page so we can show personalised content
        sessionStorage.setItem(`diplomexa.invite.${slug}`, JSON.stringify(data));
        // Smart route by detected platform
        if (data.platform_detected === "ios" && data.ios_url) {
          window.location.replace(data.ios_url);
          return;
        }
        if (data.platform_detected === "android" && data.android_url) {
          window.location.replace(data.android_url);
          return;
        }
        // Desktop / fallback: rich download page
        navigate(`/download?ref=${encodeURIComponent(slug)}`, { replace: true });
      } catch (e) {
        if (!cancelled) setError("Invite link not recognised or has been deactivated.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, navigate]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center text-white px-6"
      style={{ background: "radial-gradient(circle at 20% 0%, #14264F, #0A192F 60%, #050C1B)" }}
      data-testid="invite-redirect-page"
    >
      <BrandLogo light compact />
      {error ? (
        <div className="mt-8 text-center max-w-md">
          <p className="text-red-300 text-sm uppercase tracking-[0.22em] font-bold">Invite not found</p>
          <p className="mt-3 text-slate-400">{error}</p>
        </div>
      ) : (
        <>
          <Loader2 className="mt-10 h-6 w-6 animate-spin text-[#D4AF37]" />
          <p className="mt-4 text-sm text-slate-400">Preparing your install link…</p>
          <p className="mt-1 text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] font-bold">
            Diplomexa by Factskoot
          </p>
        </>
      )}
    </div>
  );
}
